import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";

import {
  STEPS,
  getDefaultFormData,
} from "../components/questionnaire/questionnaireConfig";
import { renderQuestionnaireStep } from "../components/questionnaire/QuestionnaireSteps";

const QuestionnairePage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [currentStep, setCurrentStep] = useState(() => {
    const saved = localStorage.getItem("questionnaire_step");
    return saved ? parseInt(saved, 10) : 0;
  });
  const [loading, setLoading] = useState(false);
  const [animating, setAnimating] = useState(false);

  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem("questionnaire_data");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return getDefaultFormData();
      }
    }
    return getDefaultFormData();
  });

  useEffect(() => {
    localStorage.setItem("questionnaire_data", JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    localStorage.setItem("questionnaire_step", currentStep.toString());
  }, [currentStep]);

  const clearSavedProgress = () => {
    localStorage.removeItem("questionnaire_data");
    localStorage.removeItem("questionnaire_step");
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const handleNext = () => {
    setAnimating(true);
    setTimeout(() => {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
      setAnimating(false);
    }, 200);
  };

  const handleBack = () => {
    setAnimating(true);
    setTimeout(() => {
      setCurrentStep((prev) => Math.max(prev - 1, 0));
      setAnimating(false);
    }, 200);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/questionnaire/submit`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const response = await axios.post(
        `${API}/recommendations/generate`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      sessionStorage.setItem(
        "expatmatch_recommendations",
        JSON.stringify(response.data.recommendations)
      );
      sessionStorage.setItem(
        "expatmatch_form_data",
        JSON.stringify(formData)
      );

      clearSavedProgress();

      toast.success("Recommendations generated!");
      navigate("/results");
    } catch (error) {
      const message =
        error.response?.data?.detail || "Failed to generate recommendations";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const toggleArrayItem = (field, item) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter((i) => i !== item)
        : [...prev[field], item],
    }));
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8 flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-primary transition-colors"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1
            className="text-3xl font-bold text-primary"
            data-testid="questionnaire-title"
          >
            Find Your Perfect Country
          </h1>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            <span className="text-sm text-muted-foreground">
              Step {currentStep + 1} of {STEPS.length}
            </span>
            <span className="text-sm font-medium text-primary">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} className="h-2" data-testid="progress-bar" />
        </div>

        {/* Step Title */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            {STEPS[currentStep].title}
          </h2>
          <p className="text-muted-foreground">
            {STEPS[currentStep].description}
          </p>
        </div>

        {/* Form Content */}
        <div className="bg-card rounded-2xl border border-primary/10 p-8 mb-8">
          {renderQuestionnaireStep({
            currentStep,
            formData,
            setFormData,
            toggleArrayItem,
            animating,
          })}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="gap-2"
            data-testid="prev-step-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Previous
          </Button>

          {currentStep === STEPS.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="btn-animated bg-secondary hover:bg-secondary/90 gap-2"
              data-testid="submit-questionnaire-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  Get Recommendations
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="btn-animated bg-primary hover:bg-primary/90 gap-2"
              data-testid="next-step-btn"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionnairePage;
